import React, { useEffect, useMemo, useState } from "react";

import LoadingSpinner from "./LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import {
  type BandwidthHistory,
  fetchBandwidthHistory,
  formatMB,
} from "../services/bandwidthApi";

/**
 * Per-box bandwidth card for the SignalDetail page. Renders a 30-day
 * up/down stacked sparkline and calls out the day the box exceeded
 * threshold (if any). Uses inline SVG — no chart lib dependency.
 *
 * Loads on mount + refreshes on `scuName` change. Silent skeleton
 * when history is empty (a new box) or the API 404s (no Peplink
 * linked).
 */
interface BandwidthCardProps {
  scuName: string;
  days?: number;
}

export const BandwidthCard: React.FC<BandwidthCardProps> = ({
  scuName, days = 30,
}) => {
  const { token } = useAuth();
  const [hist, setHist] = useState<BandwidthHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    fetchBandwidthHistory(token, scuName, days)
      .then((r) => setHist(r))
      .catch((e) => {
        // 404 (no Peplink linked) is expected for many SmartBoxes —
        // render as an empty card rather than a screaming error.
        if (/not found|no Peplink/i.test(e instanceof Error ? e.message : String(e))) {
          setHist(null);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => setLoading(false));
  }, [token, scuName, days]);

  const totalMB = useMemo(() => {
    if (!hist) return 0;
    return hist.data.reduce((s, p) => s + p.total_mb, 0);
  }, [hist]);

  const peak = useMemo(() => {
    if (!hist || hist.data.length === 0) return null;
    return hist.data.reduce((a, b) => (b.total_mb > a.total_mb ? b : a));
  }, [hist]);

  return (
    <div className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold">Cellular / WAN — last {days} days</h3>
        {hist && (
          <span className="text-xs text-secondary-500 font-mono">
            {hist.device_sn ?? `devId ${hist.device_id}`}
          </span>
        )}
      </div>
      {loading && !hist ? (
        <div className="py-6 flex justify-center"><LoadingSpinner /></div>
      ) : error ? (
        <div className="text-xs text-error-700">{error}</div>
      ) : !hist || hist.data.length === 0 ? (
        <div className="text-xs text-secondary-500 py-4">
          No Peplink bandwidth data yet for this box.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
            <Stat label="Total" value={formatMB(totalMB)} />
            <Stat
              label="Peak day"
              value={peak ? formatMB(peak.total_mb) : "—"}
              sub={peak ? peak.date : undefined}
            />
            <Stat
              label="Daily avg"
              value={formatMB(totalMB / Math.max(1, hist.data.length))}
            />
          </div>
          <Sparkline points={hist.data} height={80} />
        </>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------

const Stat: React.FC<{ label: string; value: string; sub?: string }> = ({
  label, value, sub,
}) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-secondary-500 font-semibold">
      {label}
    </div>
    <div className="font-mono font-semibold text-secondary-800">{value}</div>
    {sub && <div className="text-[10px] text-secondary-500 font-mono">{sub}</div>}
  </div>
);


// ---------------------------------------------------------------------------
// Sparkline — stacked up/down bars per day, inline SVG.
// ---------------------------------------------------------------------------

interface SparklineProps {
  points: Array<{ date: string; up_mb: number; down_mb: number; total_mb: number }>;
  height: number;
}

const Sparkline: React.FC<SparklineProps> = ({ points, height }) => {
  const width = Math.max(200, points.length * 12);
  const maxMB = Math.max(...points.map((p) => p.total_mb), 1);
  const barW = width / points.length;
  const gap = Math.min(3, barW * 0.25);
  const usableW = barW - gap;

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height + 24} className="text-primary-600">
        {/* Baseline grid — one horizontal rule at the mid + max. */}
        <line
          x1={0} x2={width} y1={height} y2={height}
          stroke="currentColor" strokeOpacity={0.15}
        />
        <line
          x1={0} x2={width} y1={height / 2} y2={height / 2}
          stroke="currentColor" strokeOpacity={0.08} strokeDasharray="2 3"
        />
        {points.map((p, i) => {
          const totalH = (p.total_mb / maxMB) * height;
          const upH = (p.up_mb / maxMB) * height;
          const downH = totalH - upH;
          const x = i * barW + gap / 2;
          const yTotal = height - totalH;
          return (
            <g key={p.date}>
              {/* Download (bottom) — lighter */}
              <rect
                x={x} y={height - downH} width={usableW} height={downH}
                fill="currentColor" opacity={0.35}
              >
                <title>
                  {p.date}: down {p.down_mb.toFixed(1)} MB, up {p.up_mb.toFixed(1)} MB
                </title>
              </rect>
              {/* Upload (top of the stack) — full color */}
              <rect
                x={x} y={yTotal} width={usableW} height={upH}
                fill="currentColor" opacity={0.85}
              >
                <title>
                  {p.date}: up {p.up_mb.toFixed(1)} MB, down {p.down_mb.toFixed(1)} MB
                </title>
              </rect>
            </g>
          );
        })}
        {/* First + last labels */}
        <text x={0} y={height + 16} className="text-[10px] fill-secondary-500">
          {points[0]?.date}
        </text>
        <text
          x={width} y={height + 16} textAnchor="end"
          className="text-[10px] fill-secondary-500"
        >
          {points[points.length - 1]?.date}
        </text>
      </svg>
      <div className="text-[10px] text-secondary-500 mt-1 flex gap-3">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-primary-600 opacity-85" />
          upload
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-primary-600 opacity-35" />
          download
        </span>
      </div>
    </div>
  );
};

export default BandwidthCard;
